/**
 * PATH: packages/bills/src/components/pdfTextCapture.tsx
 * FILE_ID: BILLS-PKG-CMP-PDFTEXTCAP-001
 * PURPOSE: The "pick a value off the PDF" layer — read a page's text runs
 *          (with positions) from pdf.js, and render an interactive overlay
 *          that lets the operator CLICK one run or DRAG a box around several
 *          to capture the exact text under the cursor.
 * WHY: The backend extractor emits no coordinates yet, so the only source of
 *      truth for "what text is here on the page" is pdf.js's own text layer.
 *      Operator ask: "select it from the pdf with highlighted boxes, read
 *      around it and it auto fills." Click = one run; drag = read everything
 *      inside the box (multi-word / multi-line descriptions).
 *
 * FLOW:
 *   page.getTextContent() ──▶ TextBox[] (normalised 0..1 bboxes)
 *   pointer (click | drag) ──▶ hit-test boxes ──▶ onPick(joined text)
 *
 * DEPENDENCIES:
 *   - pdfjs-dist (legacy build): Util.transform for text→viewport mapping.
 * RELATED:
 *   - PdfCanvasViewer.tsx: extracts boxes per page + mounts this layer.
 *   - captureValue.ts: coerces the picked text into the field's shape.
 */

import { useEffect, useRef, useState } from 'react';
import type { BillFieldRegion } from '../types';
import { isClickGesture } from './captureRouting';
import { fieldRegionAtPoint } from './pdfFieldRegions';
import {
  boxAtPoint,
  boxesInRect,
  clamp01,
  joinBoxesInRect,
  pointOnTextRow,
  rectFromPoints,
  type Rect,
  type TextBox,
} from './pdfTextGeometry';

// Keep a single import surface for callers/tests: the page-geometry maths now
// lives in pdfTextGeometry.ts (pure, unit-tested); this file owns only the
// React overlay below. Re-export so existing `from './pdfTextCapture'` imports
// (PdfCanvasViewer, the geometry tests) keep working unchanged.
export {
  boxAtPoint,
  boxesInRect,
  extractTextBoxes,
  joinBoxesInRect,
  pointOnTextRow,
  rectFromPoints,
} from './pdfTextGeometry';
export type { Rect, TextBox } from './pdfTextGeometry';

/**
 * Always-on pick overlay for one page.
 *
 * Interaction model (extended 2026-06-22 — operator: "I tried selecting …
 * multiple lines in [the] description … yet I could not just drag over it and
 * [have] it put in the name"):
 *   - The overlay is ALWAYS present whenever the page has text — no hidden
 *     "capture mode". Hovering reveals the pickable runs (faint boxes) and
 *     solidifies the run under the cursor.
 *   - A CLICK on a run grabs that one run.
 *   - A DRAG that STARTS on a line of text draws a selection box and grabs
 *     EVERY run it covers, joined in reading order — the multi-word /
 *     multi-line descriptions operators kept trying to sweep over.
 *   - A DRAG that starts in the blank margins is a PAN (a zoomed page still
 *     moves), handled by `useDragPan` on the scroll wrapper.
 *
 * Pan vs select must be decided BEFORE `useDragPan`'s wrapper listener fires.
 * useDragPan binds a bubble-phase `mousedown` on the wrapper (our ancestor), so
 * we bind ours in the CAPTURE phase on the overlay: a press on text calls
 * `stopPropagation()` (the wrapper never sees it → no pan) and we own the
 * gesture; a press on whitespace is left untouched so it bubbles up and pans.
 */
export function CapturePageLayer({
  boxes,
  onPick,
  pannable = false,
  activeRects,
  regions,
  activeFieldKey,
}: {
  boxes: TextBox[];
  /**
   * Picked text + the normalised page point where the gesture STARTED. The
   * point lets the caller hit-test which field region was clicked, so a bare
   * click routes straight to that field (no arming needed). ``gesture``
   * distinguishes a bare click from a drag-sweep — a click on a known field
   * region opens the in-place editor, a drag carries the swept text.
   */
  onPick: (text: string, at?: { x: number; y: number }, gesture?: 'click' | 'drag') => void;
  /** When the page can pan (zoom > fit), show a grab cursor off-text. */
  pannable?: boolean;
  /**
   * The ACTIVE field's highlight rect(s) on this page. Runs inside them skip
   * their own hover ring — the region border already frames the text, and
   * stacking a second outline read as "two bounded boxes" (2026-07-24).
   */
  activeRects?: Rect[];
  /**
   * This page's field regions. Hovering INSIDE one lights up the WHOLE
   * field (every row box), because that is what a click will edit —
   * hovering only the word under the cursor misled operators about the
   * click's scope (2026-07-25 "it selects the word or letter i hovered on
   * instead of the entire line item").
   */
  regions?: BillFieldRegion[];
  /** The armed/active field — already framed by RegionBox, so hover skips it. */
  activeFieldKey?: string | null;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [within, setWithin] = useState(false);
  const [hover, setHover] = useState<TextBox | null>(null);
  const [hoverField, setHoverField] = useState<string | null>(null);
  // Live marquee (normalised) while a text drag is in progress.
  const [selRect, setSelRect] = useState<Rect | null>(null);

  // Latest props for the natively-bound gesture handlers (bound once on mount).
  const boxesRef = useRef(boxes);
  boxesRef.current = boxes;
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const regionsRef = useRef(regions);
  regionsRef.current = regions;

  const normAt = (clientX: number, clientY: number) => {
    const el = ref.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return {
      x: clamp01((clientX - r.left) / (r.width || 1)),
      y: clamp01((clientY - r.top) / (r.height || 1)),
    };
  };

  // Capture-phase press handler — see the component doc for why it must be the
  // capture phase (it has to decide pan-vs-select before useDragPan reacts).
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onDownCapture = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const start = normAt(e.clientX, e.clientY);
      const onText = pointOnTextRow(boxesRef.current, start.x, start.y);
      // Region padding (the highlight box around a value) must also own the
      // gesture — requiring an exact text-run hit made boxes feel dead when
      // the operator clicked the wash rather than the glyph ("bounded boxes
      // not working", 2026-08-10).
      const onRegion = regionsRef.current?.length
        ? fieldRegionAtPoint(regionsRef.current, start.x, start.y)
        : null;
      // Press in the blank margins → leave the event alone so the wrapper pans.
      if (!onText && !onRegion) return;
      // Press on text / a known field → we own the gesture: suppress pan + native selection.
      e.stopPropagation();
      e.preventDefault();
      const startClient = { x: e.clientX, y: e.clientY };
      let moved = false;

      const onMoveWin = (m: MouseEvent) => {
        if (!isClickGesture(m.clientX - startClient.x, m.clientY - startClient.y)) {
          moved = true;
        }
        const cur = normAt(m.clientX, m.clientY);
        setSelRect(rectFromPoints(start.x, start.y, cur.x, cur.y));
      };
      const onUpWin = (u: MouseEvent) => {
        window.removeEventListener('mousemove', onMoveWin);
        window.removeEventListener('mouseup', onUpWin);
        setSelRect(null);
        const dragged =
          moved && !isClickGesture(u.clientX - startClient.x, u.clientY - startClient.y);
        if (dragged) {
          const cur = normAt(u.clientX, u.clientY);
          const text = joinBoxesInRect(
            boxesRef.current,
            rectFromPoints(start.x, start.y, cur.x, cur.y),
          );
          if (text) onPickRef.current(text, start, 'drag');
        } else {
          // No real travel → treat as a click on the run under the press
          // (or an empty string when the press landed in region padding so
          // the caller still opens the in-place editor for that field).
          const hit = boxAtPoint(boxesRef.current, start.x, start.y);
          onPickRef.current(hit?.str ?? '', start, 'click');
        }
      };
      window.addEventListener('mousemove', onMoveWin);
      window.addEventListener('mouseup', onUpWin);
    };
    el.addEventListener('mousedown', onDownCapture, true);
    return () => el.removeEventListener('mousedown', onDownCapture, true);
    // Bound once; latest boxes/onPick/regions are read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMove = (e: React.MouseEvent) => {
    if (selRect) return; // during a drag the marquee leads, not the hover
    const p = normAt(e.clientX, e.clientY);
    setHover(boxAtPoint(boxes, p.x, p.y));
    setHoverField(regions?.length ? fieldRegionAtPoint(regions, p.x, p.y) : null);
  };
  const onEnter = () => setWithin(true);
  const onLeave = () => {
    setWithin(false);
    setHover(null);
    setHoverField(null);
  };

  const selected = selRect ? boxesInRect(boxes, selRect) : [];
  // Row boxes of the field under the cursor (skipping the active field,
  // which RegionBox already frames — no stacked outlines).
  const hoverFieldRects =
    !selRect && hoverField && hoverField !== activeFieldKey
      ? (regions ?? []).filter((r) => r.field === hoverField)
      : [];
  const cursor = hoverFieldRects.length
    ? 'pointer'
    : selRect || hover
      ? 'crosshair'
      : pannable
        ? 'grab'
        : 'default';
  // Is a run already framed by the ACTIVE field's region box? Then its hover
  // treatment drops the ring (fill only) so the two never stack into a
  // double outline.
  const inActiveRegion = (b: TextBox) => {
    if (!activeRects?.length) return false;
    const cx = (b.x0 + b.x1) / 2;
    const cy = (b.y0 + b.y1) / 2;
    return activeRects.some((r) => cx >= r.x0 && cx <= r.x1 && cy >= r.y0 && cy <= r.y1);
  };

  return (
    <div
      ref={ref}
      className="absolute inset-0 z-20 select-none"
      style={{ cursor }}
      onMouseMove={onMove}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {/* Reveal every pickable run while the pointer is over the document —
          a quiet marker-style wash, no outline. Boxed outlines at rest made
          the page read as a grid of nested rectangles. */}
      {within &&
        boxes.map((b, i) => (
          <span
            key={i}
            className="pointer-events-none absolute bg-primary/[0.07]"
            style={pct(b)}
          />
        ))}
      {/* Runs the live marquee covers solidify, so it's obvious what a release
          will grab (multi-line description sweeps). */}
      {selected.map((b, i) => (
        <span
          key={`sel-${i}`}
          className="pointer-events-none absolute bg-primary/25 ring-1 ring-primary/70"
          style={pct(b)}
        />
      ))}
      {/* The drag selection box itself. */}
      {selRect && (
        <span
          className="pointer-events-none absolute border border-dashed border-primary/70 bg-primary/5"
          style={pct(selRect)}
        />
      )}
      {/* Hovering inside a known field lights the WHOLE field (every row
          box) — that is exactly what a click will edit. */}
      {hoverFieldRects.map((r, i) => (
        <span
          key={`hf-${i}`}
          className="pointer-events-none absolute bg-primary/10 ring-1 ring-primary/60"
          style={pct({ x0: r.bbox[0], y0: r.bbox[1], x1: r.bbox[2], y1: r.bbox[3] })}
        />
      ))}
      {/* Single-run hover when not dragging and not over a field region:
          crisp ring — unless the active region already frames this text
          (fill only, no second outline). */}
      {!selRect && hover && !hoverFieldRects.length && (
        <span
          className={
            inActiveRegion(hover) || hoverField === activeFieldKey
              ? 'pointer-events-none absolute bg-primary/15'
              : 'pointer-events-none absolute bg-primary/15 ring-1 ring-primary/70 shadow-sm'
          }
          style={pct(hover)}
        />
      )}
    </div>
  );
}

function pct(b: { x0: number; y0: number; x1: number; y1: number }) {
  return {
    left: `${b.x0 * 100}%`,
    top: `${b.y0 * 100}%`,
    width: `${(b.x1 - b.x0) * 100}%`,
    height: `${(b.y1 - b.y0) * 100}%`,
  };
}
